import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import './QuickReading.css';
import QUICK_READING_CARDS from '../../data/tarotCards';

const cardMeaningPath = (name) => `/tarot-card-meanings/${name.toLowerCase().replace(/\s+/g, '-')}/`;

const POSITIONS = ['Past', 'Present', 'Future'];

function shuffleArray(arr) {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

const QuickReading = ({ onWantMoreClick }) => {
  const [deck, setDeck] = useState(() => shuffleArray(QUICK_READING_CARDS));
  const [flipped, setFlipped] = useState([false, false, false]);
  const [showResults, setShowResults] = useState(false);

  const selectedCards = useMemo(() => deck.slice(0, 3), [deck]);
  const allRevealed = flipped.every(Boolean);

  const handleFlip = useCallback((index) => {
    if (flipped[index]) return;
    const next = [...flipped];
    next[index] = true;
    setFlipped(next);

    if (next.every(Boolean)) {
      setTimeout(() => setShowResults(true), 600);
    }
  }, [flipped]);

  const handleReset = useCallback(() => {
    setFlipped([false, false, false]);
    setShowResults(false);
    setTimeout(() => setDeck(shuffleArray(QUICK_READING_CARDS)), 400);
  }, []);

  return (
    <div className="quick-reading">
      <h2>Pick Your Cards</h2>
      <p className="quick-reading-subtitle">
        Focus on a question, then tap each card to reveal your Past, Present, and Future.
      </p>

      <div className="card-row">
        {selectedCards.map((card, i) => (
          <div key={`${card.imageCode}-${i}`} className="reading-card-wrapper">
            <div
              className={`reading-card ${flipped[i] ? 'flipped' : ''}`}
              onClick={() => handleFlip(i)}
              role="button"
              tabIndex={0}
              aria-label={flipped[i] ? card.name : `Reveal ${POSITIONS[i]} card`}
              onKeyDown={(e) => e.key === 'Enter' && handleFlip(i)}
            >
              <div className="reading-card-inner">
                <div className="reading-card-back">
                  <div className="card-back-design">
                    <span className="card-back-symbol">✦</span>
                  </div>
                </div>
                <div
                  className="reading-card-front"
                  style={{
                    backgroundImage: `url(/tarot-reader/images/small/${card.imageCode}.png)`
                  }}
                />
              </div>
            </div>
            <div className="position-label">{POSITIONS[i]}</div>
          </div>
        ))}
      </div>

      {!allRevealed && (
        <p className="tap-hint">
          {flipped.filter(Boolean).length === 0
            ? 'Tap a card to begin your reading'
            : `${3 - flipped.filter(Boolean).length} card${3 - flipped.filter(Boolean).length > 1 ? 's' : ''} remaining`}
        </p>
      )}

      <div className={`reading-results ${showResults ? 'visible' : ''}`}>
        {showResults && (
          <>
            <h3>Your Reading</h3>
            <div className="results-grid">
              {selectedCards.map((card, i) => (
                <div key={card.imageCode} className="result-card">
                  <div className="result-card-header">
                    <img
                      src={`/tarot-reader/images/small/${card.imageCode}.png`}
                      alt={card.name}
                      className="result-card-image"
                    />
                    <div>
                      <span className="result-position">{POSITIONS[i]}</span>
                      <h4 className="result-name">{card.name}</h4>
                    </div>
                  </div>
                  <p className="result-keywords">{card.keywords.join(' · ')}</p>
                  <p className="result-meaning">{card.meaning}</p>
                  <p className="result-meaning-link">
                    <Link to={cardMeaningPath(card.name)}>
                      Full {card.name} meaning →
                    </Link>
                  </p>
                </div>
              ))}
            </div>

            <div className="reading-upsell">
              <h3>Want Deeper Answers?</h3>
              <p>This was a glimpse. Ask the tarot LLM a full question and get a personalized answer in chat.</p>
              <button className="upsell-cta" onClick={onWantMoreClick}>
                🔮 Ask the AI Tarot Reader
              </button>
              <p className="upsell-note">Instant AI tarot chat · No credit card required</p>
            </div>

            <button className="shuffle-again-btn" onClick={handleReset}>
              ↻ Shuffle &amp; Draw Again
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default QuickReading;
